/**
 * RAELA AI Client — internal abstraction over the underlying provider.
 *
 * RULES (locked):
 *   - RAELA is the only name members ever see. Never surface the
 *     underlying provider in member-facing copy, UI, FAQ, policies,
 *     or marketing.
 *   - All AI calls route through edge functions on the external
 *     Supabase project, which read provider keys from app_secrets.
 *     Provider is swappable at the edge layer with one config line.
 *   - The frontend never holds an AI provider key.
 *
 * This file is the single import point for any RAELA call from the
 * client. Edge functions invoke a sibling abstraction server-side.
 */
import { supabase } from "@/integrations/supabase/client";

export type RaelaRole = "system" | "user" | "assistant";

export interface RaelaMessage {
  role: RaelaRole;
  content: string;
}

import type { AppId } from "@/lib/apps";

export interface RaelaCompletionInput {
  /** Conversation history. The edge function injects RAELA's system prompt. */
  messages: RaelaMessage[];
  /** Which app the conversation lives in (drives crisis-aware routing). */
  app: AppId;
  /** Optional hint so the edge can pick the right persona variant. */
  surface?:
    | "chat"
    | "onboarding"
    | "moderation"
    | "discovery"
    | "safety"
    | "faq";
  /** Member id for audit + crisis escalation; auto-attached when omitted. */
  memberId?: string;
}

export interface RaelaCompletionOutput {
  /** Final assistant text to render. Already crisis-screened server-side. */
  text: string;
  /** True if the edge function detected crisis content and surfaced resources. */
  crisis: boolean;
  /** Opaque trace id for support. */
  traceId: string;
}

/**
 * Send a completion to RAELA via the `raela-chat` edge function.
 * Throws on transport / non-2xx; callers should surface a friendly
 * "RAELA is having trouble — try again" message and never the raw error.
 */
export const raelaChat = async (
  input: RaelaCompletionInput,
): Promise<RaelaCompletionOutput> => {
  const { data, error } = await supabase.functions.invoke<RaelaCompletionOutput>(
    "raela-chat",
    { body: input },
  );
  if (error) throw error;
  if (!data) throw new Error("RAELA returned no data");
  return data;
};

export const aiClient = { raelaChat };
